import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, GitBranch, Plus } from 'lucide-react';
import { Button } from '@/components/Button';
import { cn } from '@/lib/cn';
import { BarraDeProgresso, EtiquetaDePrioridade, EtiquetaDeStatus, EtiquetaDeTipo } from '@/features/tickets/badges';
import { mensagemDeErro } from '@/lib/format';
import { LinkDeChamado } from '@/features/tickets/navigation';
import { NewTicketDialog } from '@/features/tickets/NewTicketDialog';
import { chaves, obterArvore, verificarFechamento } from '@/features/tickets/tickets.api';
import type { Chamado } from '@/features/tickets/types';

/**
 * Subchamados e progresso consolidado.
 *
 * Usa `/tree` em vez de `/children`: o progresso do pai depende da subárvore
 * inteira, e duas chamadas — uma para os filhos, outra para o rollup — podem
 * responder de instantes diferentes e mostrar "3 de 4" ao lado de cinco linhas.
 */
export function ChildrenPanel({ chamado }: { chamado: Chamado }) {
  const [criando, setCriando] = useState(false);

  const arvore = useQuery({
    queryKey: chaves.arvore(chamado.id),
    queryFn: () => obterArvore(chamado.id),
  });

  const fechamento = useQuery({
    queryKey: chaves.fechamento(chamado.id),
    queryFn: () => verificarFechamento(chamado.id),
  });

  if (arvore.isPending) {
    return <p className="text-sm text-muted-foreground">Carregando subchamados…</p>;
  }

  if (arvore.isError) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {mensagemDeErro(arvore.error)}
      </p>
    );
  }

  const { descendants, rollup } = arvore.data;
  const concluidos = rollup.childCount - rollup.openChildCount;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-[14rem] flex-1 space-y-2">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="font-medium">Progresso consolidado</span>
            <span className="font-mono text-xs text-muted-foreground">{rollup.progressPct}%</span>
          </div>
          <BarraDeProgresso percentual={rollup.progressPct} />
          <p className="text-xs text-muted-foreground">
            {rollup.childCount === 0
              ? 'Nenhum subchamado.'
              : `${concluidos} de ${rollup.childCount} filhos diretos concluídos · ${descendants.length} na subárvore`}
          </p>
        </div>

        <Button
          size="sm"
          variant="secondary"
          onClick={() => setCriando(true)}
          icon={<Plus className="h-4 w-4" aria-hidden />}
        >
          Novo subchamado
        </Button>
      </div>

      {fechamento.data ? (
        <div
          className={cn(
            'flex items-start gap-2 rounded-md border px-3 py-2 text-sm',
            fechamento.data.allowed
              ? 'border-success/25 bg-success/10 text-success'
              : 'border-warning/30 bg-warning/10 text-warning',
          )}
        >
          {fechamento.data.allowed ? (
            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none" aria-hidden />
          ) : (
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" aria-hidden />
          )}
          <span>
            {fechamento.data.allowed
              ? 'Este chamado pode ser encerrado agora.'
              : (fechamento.data.reason ?? 'Ainda não pode ser encerrado.')}
          </span>
        </div>
      ) : null}

      {descendants.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-border px-5 py-10 text-center">
          <GitBranch className="h-5 w-5 text-muted-foreground" aria-hidden />
          <p className="text-sm text-muted-foreground">
            Quebre o trabalho em subchamados quando ele passar por mais de uma equipe.
          </p>
        </div>
      ) : (
        <ul className="space-y-1.5">
          {descendants.map((filho) => (
            <li
              key={filho.id}
              // A indentação é relativa à raiz aberta, não à profundidade
              // absoluta: um subchamado visto pelo próprio detalhe começaria
              // deslocado sem motivo.
              style={{ marginLeft: `${(filho.depth - chamado.depth - 1) * 1.25}rem` }}
              className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border border-border bg-background px-3 py-2.5"
            >
              <LinkDeChamado
                ticketId={filho.id}
                className="font-mono text-xs font-semibold text-primary hover:underline"
              >
                {filho.number}
              </LinkDeChamado>
              <LinkDeChamado ticketId={filho.id} className="min-w-0 flex-1 truncate text-sm hover:underline">
                {filho.subject}
              </LinkDeChamado>
              <EtiquetaDeTipo tipo={filho.type} />
              <EtiquetaDePrioridade prioridade={filho.priority} />
              <EtiquetaDeStatus status={filho.statusCategory} />
            </li>
          ))}
        </ul>
      )}

      <NewTicketDialog
        aberto={criando}
        aoFechar={() => setCriando(false)}
        pai={{ id: chamado.id, number: chamado.number }}
      />
    </div>
  );
}
